import * as React from 'react';
import { KitSkortPattern, KitSkortSideTrianglesMode } from 'Src/types/kitLab';
import { outlineStrokeProps } from 'Src/helpers/kitLab/kitLab';

interface Props {
  pattern?: KitSkortPattern;
  primaryColor: string;
  secondaryColor: string;
  stripeCount?: number;
  stripesEqualWidth?: boolean;
  stripePattern?: number[];
  sideTrianglesMode?: KitSkortSideTrianglesMode;
  className?: string;
}

export const KitSkortPreview: React.FC<Props> = ({
  pattern = 'plain',
  primaryColor,
  secondaryColor,
  stripeCount = 5,
  stripesEqualWidth = true,
  stripePattern,
  sideTrianglesMode = 'BOTH',
  className
}) => {
  const skortClassName = ['eKitLabSkortPreview', className].filter(Boolean).join(' ');

  const waistY = 26;
  const skirtTopY = waistY;
  const skirtBottomY = 70;

  const leftTopX = 34;
  const rightTopX = 86;
  const leftBottomX = 24;
  const rightBottomX = 96;

  const skirtPath = `
    M ${leftTopX} ${skirtTopY}
    L ${rightTopX} ${skirtTopY}
    L ${rightBottomX} ${skirtBottomY}
    L ${leftBottomX} ${skirtBottomY}
    Z
  `;

  const leftXAtY = (y: number) => {
    const t = (y - skirtTopY) / (skirtBottomY - skirtTopY);
    return leftTopX + (leftBottomX - leftTopX) * t;
  };

  const rightXAtY = (y: number) => {
    const t = (y - skirtTopY) / (skirtBottomY - skirtTopY);
    return rightTopX + (rightBottomX - rightTopX) * t;
  };

  const clipIdRef = React.useRef<string>();
  if (!clipIdRef.current) {
    clipIdRef.current = `skortClip-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
  }
  const clipId = clipIdRef.current;

  const renderVerticalStripes = () => {
    if (pattern !== 'verticalStripes') return null;

    const count = Math.max(2, stripeCount);
    const usePattern = !stripesEqualWidth && stripePattern && stripePattern.length === count;

    const weights = usePattern ? stripePattern! : Array(count).fill(1);
    const total = weights.reduce((s, w) => s + (w || 1), 0);
    const baseWidth = rightBottomX - leftBottomX;

    let acc = 0;

    return (
      <g clipPath={`url(#${clipId})`}>
        {weights.map((w, i) => {
          const startRatio = acc / total;
          const widthRatio = (w || 1) / total;
          acc += w || 1;

          const x = leftBottomX + baseWidth * startRatio;
          const wPx = baseWidth * widthRatio;

          const isSecondary = i % 2 === 0;
          const fill = isSecondary ? secondaryColor : primaryColor;

          return <rect key={i} x={x} y={skirtTopY} width={wPx + 0.5} height={skirtBottomY - skirtTopY} fill={fill} />;
        })}
      </g>
    );
  };

  const renderSideStripes = () => {
    if (pattern !== 'sideStripes') return null;

    const stripeTopY = skirtTopY + 2;
    const stripeBottomY = skirtBottomY - 2;
    const stripeWidth = 6;

    return (
      <>
        <polygon
          points={`
            ${leftTopX + 2},${stripeTopY}
            ${leftTopX + 2 + stripeWidth},${stripeTopY}
            ${leftBottomX + 2 + stripeWidth},${stripeBottomY}
            ${leftBottomX + 2},${stripeBottomY}
          `}
          fill={secondaryColor}
        />
        <polygon
          points={`
            ${rightTopX - 2 - stripeWidth},${stripeTopY}
            ${rightTopX - 2},${stripeTopY}
            ${rightBottomX - 2},${stripeBottomY}
            ${rightBottomX - 2 - stripeWidth},${stripeBottomY}
          `}
          fill={secondaryColor}
        />
      </>
    );
  };

  const renderSideTriangles = () => {
    if (pattern !== 'sideTriangles') return null;

    const topTriHeight = 14;
    const bottomTriHeight = 16;

    const topBaseY = skirtTopY;
    const bottomBaseY = skirtBottomY - 1;

    const topTipY = topBaseY + topTriHeight;
    const bottomTipY = bottomBaseY - bottomTriHeight;

    const leftTopBaseOuterX = leftXAtY(topBaseY);
    const leftTopBaseInnerX = leftTopBaseOuterX + 13;
    const leftTopTipX = leftXAtY(topTipY);

    const rightTopBaseOuterX = rightXAtY(topBaseY);
    const rightTopBaseInnerX = rightTopBaseOuterX - 13;
    const rightTopTipX = rightXAtY(topTipY);

    const leftBottomBaseOuterX = leftXAtY(bottomBaseY);
    const leftBottomBaseInnerX = leftBottomBaseOuterX + 14;
    const leftBottomTipX = leftXAtY(bottomTipY);

    const rightBottomBaseOuterX = rightXAtY(bottomBaseY);
    const rightBottomBaseInnerX = rightBottomBaseOuterX - 14;
    const rightBottomTipX = rightXAtY(bottomTipY);

    const showTop = sideTrianglesMode === 'TOP' || sideTrianglesMode === 'BOTH';
    const showBottom = sideTrianglesMode === 'BOTTOM' || sideTrianglesMode === 'BOTH';

    return (
      <>
        {showTop && (
          <>
            <polygon
              points={`
                ${leftTopBaseOuterX},${topBaseY}
                ${leftTopBaseInnerX},${topBaseY}
                ${leftTopTipX},${topTipY}
              `}
              fill={secondaryColor}
            />
            <polygon
              points={`
                ${rightTopBaseInnerX},${topBaseY}
                ${rightTopBaseOuterX},${topBaseY}
                ${rightTopTipX},${topTipY}
              `}
              fill={secondaryColor}
            />
          </>
        )}

        {showBottom && (
          <>
            <polygon
              points={`
                ${leftBottomBaseOuterX},${bottomBaseY}
                ${leftBottomBaseInnerX},${bottomBaseY}
                ${leftBottomTipX},${bottomTipY}
              `}
              fill={secondaryColor}
            />
            <polygon
              points={`
                ${rightBottomBaseInnerX},${bottomBaseY}
                ${rightBottomBaseOuterX},${bottomBaseY}
                ${rightBottomTipX},${bottomTipY}
              `}
              fill={secondaryColor}
            />
          </>
        )}
      </>
    );
  };

  const renderSideBottomTriangles = () => {
    if (pattern !== 'sideBottomTriangles') return null;

    const baseY = skirtBottomY;
    const tipY = skirtTopY;
    const triWidth = 14;

    const leftTipX = leftXAtY(tipY);
    const leftBaseOuterX = leftXAtY(baseY);
    const leftBaseInnerX = leftBaseOuterX + triWidth;

    const rightTipX = rightXAtY(tipY);
    const rightBaseOuterX = rightXAtY(baseY);
    const rightBaseInnerX = rightBaseOuterX - triWidth;

    return (
      <g clipPath={`url(#${clipId})`}>
        <polygon
          points={`
            ${leftTipX},${tipY}
            ${leftBaseOuterX},${baseY}
            ${leftBaseInnerX},${baseY}
          `}
          fill={secondaryColor}
        />

        <polygon
          points={`
            ${rightTipX},${tipY}
            ${rightBaseInnerX},${baseY}
            ${rightBaseOuterX},${baseY}
          `}
          fill={secondaryColor}
        />
      </g>
    );
  };

  const renderSideSemiCircles = () => {
    if (pattern !== 'sideSemiCircles') return null;

    const height = skirtBottomY - skirtTopY;
    const radius = height / 2 + 1;
    const centerY = (skirtTopY + skirtBottomY) / 2;
    const offsetFactor = 0.55;

    const leftCenterX = leftXAtY(centerY) - radius * offsetFactor;
    const rightCenterX = rightXAtY(centerY) + radius * offsetFactor;

    return (
      <g clipPath={`url(#${clipId})`}>
        <circle cx={leftCenterX} cy={centerY} r={radius} fill={secondaryColor} />
        <circle cx={rightCenterX} cy={centerY} r={radius} fill={secondaryColor} />
      </g>
    );
  };

  const renderPleats = () => {
    if (pattern !== 'plain') return null;

    const foldsColor = '#ffffff';
    const foldsOpacity = 0.65;

    return (
      <>
        <path
          d={`M ${leftTopX + 10} ${skirtTopY + 2} L ${leftBottomX + 6} ${skirtBottomY - 2}`}
          stroke={foldsColor}
          strokeWidth={2}
          strokeOpacity={foldsOpacity}
        />
        <path
          d={`M 60 ${skirtTopY + 2} L 60 ${skirtBottomY - 2}`}
          stroke={foldsColor}
          strokeWidth={2}
          strokeOpacity={foldsOpacity}
        />
        <path
          d={`M ${rightTopX - 10} ${skirtTopY + 2} L ${rightBottomX - 6} ${skirtBottomY - 2}`}
          stroke={foldsColor}
          strokeWidth={2}
          strokeOpacity={foldsOpacity}
        />
      </>
    );
  };

  return (
    <svg
      className={skortClassName}
      viewBox="0 0 120 96"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <clipPath id={clipId}>
          <path d={skirtPath} />
        </clipPath>
      </defs>

      <path d={skirtPath} fill={primaryColor} stroke="none" />

      {renderVerticalStripes()}
      {renderSideStripes()}
      {renderSideTriangles()}
      {renderSideBottomTriangles()}
      {renderSideSemiCircles()}
      {renderPleats()}

      <path d={skirtPath} fill="none" {...outlineStrokeProps} />
    </svg>
  );
};
